<template>
  <form v-on:submit.prevent="create">
    <ConfigurationForm
      v-on:form-errors="disabledTitle = $event"
      :worker-version-id="workerVersionId"
      :worker-id="workerId"
      v-model="formConfiguration"
    />
    <div
      class="help is-danger mb-2"
      v-if="conflictError"
    >
      <p>
        {{ conflictError.errors[0].detail }}
        <a
          v-if="conflictError.existing_id"
          v-on:click="$emit('set-configuration', conflictError.existing_id)"
          >View this configuration</a
        >
      </p>
    </div>
    <button
      type="button"
      class="button is-success"
      :disabled="disabledTitle.length > 0 || undefined"
      v-on:click="create"
      :title="disabledTitle || 'Create a new configuration'"
    >
      Create
    </button>
  </form>
</template>

<script lang="ts">
import { cloneDeep, isEmpty } from 'lodash'
import { mapActions, mapState, mapStores } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import ConfigurationForm from '@/components/Process/Workers/Configurations/ConfigurationForm/Form.vue'
import { errorParser, isArkindexConflictError } from '@/helpers'
import { useNotificationStore, useWorkerConfigurationStore, useWorkerStore } from '@/stores'
import type { ArkindexConflictErrorResponse, UUID } from '@/types'

export default defineComponent({
  emits: ['set-configuration', 'config-created'],
  components: {
    ConfigurationForm,
  },
  props: {
    workerVersionId: {
      type: String as PropType<UUID>,
      required: true,
    },
    workerId: {
      type: String as PropType<UUID>,
      required: true,
    },
    newConfiguration: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    loading: false,
    conflictError: null as ArkindexConflictErrorResponse | null,
    disabledTitle: '',
    formConfiguration: {
      name: '',
      configuration: {},
    },
  }),
  computed: {
    ...mapState(useWorkerStore, ['workerVersions']),
    ...mapStores(useWorkerConfigurationStore),
  },
  methods: {
    ...mapActions(useNotificationStore, ['notify']),
    async create() {
      if (this.loading || this.disabledTitle) return
      this.loading = true
      try {
        const configuration = await this.workerConfigurationStore.create(this.workerId, {
          name: this.formConfiguration.name,
          configuration: this.formConfiguration.configuration,
        })
        // Go back to configurations list only if the creation succeeded
        this.$emit('config-created')
        // Select the newly created configuration
        this.$emit('set-configuration', configuration.id)
        this.notify({
          type: 'info',
          text: 'Configuration created. Save to add it to this worker run.',
        })
      } catch (err) {
        if (isArkindexConflictError(err)) {
          this.conflictError = err.response.data
        }
        this.notify({ type: 'error', text: errorParser(err) })
        this.loading = false
      }
    },
  },
  watch: {
    newConfiguration: {
      immediate: true,
      handler(newValue) {
        if (!isEmpty(newValue.configuration)) this.formConfiguration = cloneDeep(newValue)
      },
    },
  },
})
</script>
